/**
 * TODO: 
 * - add onloadscreen
 */

import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import gsap from 'gsap';

import Sound from './Sound';
import pathToModel from '../../assets/models/forest.glb';
import texture2 from '../../assets/img//textures/grid.jpg';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.clock = new THREE.Clock();
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/010b13-f5f7f7-262422-5fc5e1-f46e42');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.isDisposed = false;
        this.isAnimate = true;

        this.scene.background = this.colourArr[0];
        this.scene.fog = new THREE.Fog(this.colourArr[0], 150, 350);
        this.loader = new THREE.TextureLoader();

        this.setupCamera();
        this.setupLight();
        this.loadModels();
        this.setupControls();
        this.setupGeometry();
        this.setupSound(this.renderer.domElement);
    }

    setupCamera() {
        let VIEW_ANGLE = 50;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 0.1;
        let FAR_PLANE = 1000;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(-113, 57, 148);
    }

    setupLight() {
        const hemiLight = new THREE.HemisphereLight(this.colourArr[2], this.colourArr[2], 1);
        hemiLight.groundColor.setHSL(0.78, 0.2, 1.);
        hemiLight.color.setHSL(0.67, 0.19, 0.75);
        hemiLight.position.set(0, 50, 0);
        this.scene.add(hemiLight);

        const spotLight = new THREE.SpotLight();
        spotLight.angle = Math.PI / 4;
        spotLight.penumbra = 0.25;
        spotLight.decay = 2;
        spotLight.distance = 200;

        spotLight.castShadow = true;
        spotLight.shadow.mapSize.width = 1024;
        spotLight.shadow.mapSize.height = 1024;
        spotLight.shadow.camera.near = 10;
        spotLight.shadow.camera.far = 100;
        spotLight.shadow.camera.fov = 30;

        spotLight.color.set(0xBBFFEE).multiplyScalar(0.66);
        spotLight.position.lerp(this.camera.position, 0.75);
        this.scene.add(spotLight);
    }

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = false;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 400;
        this.controls.minDistance = 100;
        this.controls.maxPolarAngle = 1.2737424473141494;
        this.controls.minPolarAngle = 1.2737424473141494;
    }

    setupShaderMaterial() {
        this.uniforms = {
            u_resolution: { type: "v2", value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
            u_time: { type: "f", value: 0 },
            u_color0: { type: "v3", value: new THREE.Color(0xff0000) },
            u_color1: { type: "v3", value: new THREE.Color(0x6fa8dc) },
        };

        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                varying vec3 vNormal;
                varying vec2 vUv; 
            
                void main()
                {
                    vNormal = normal;
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0 );
                }
            `,
            fragmentShader: `
                precision mediump float;

                uniform float u_time;
                uniform vec2 u_resolution;

                varying vec2 vUv;
                varying vec3 vNormal;

                const float fluid_speed = 200.0;
                const float color_intensity = 0.5;

                float rand(vec2 co)
                {
                    return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);
                }

                void main()
                {
                    vec2 pos = vUv * (gl_FragCoord.xy/u_resolution);

                    for(int i=1; i<45; i++)
                    {
                        vec2 newPos = pos + u_time * 0.02;
                        newPos.x+=0.6/float(i)*sin(float(i)*pos.y+u_time/fluid_speed+0.3*float(i)) + 0.5;
                        newPos.y+=0.6/float(i)*sin(float(i)*pos.x+u_time/fluid_speed+0.3*float(i+10)) - 0.5;
                        pos = newPos;
                    }

                    vec3 n = vNormal;
                    vec3 col = vec3(color_intensity * sin(0.5 * pos.x) + n.x, 
                                    color_intensity * sin(pos.x + pos.y),
                                    color_intensity * sin(1.0 * pos.y) + n.z);
                    //gl_FragColor=vec4(col, 1.0);
                    gl_FragColor=vec4(col * vNormal, 1.0);
                }
            `
        });
        return material;
    }

    loadModels() {
        let group = new THREE.Object3D();
        this.scene.add(group);
        group.position.set(0, -4, -30);

        let model1;
        const gltfLoader = new GLTFLoader();
        gltfLoader.load(pathToModel, (gltf) => {
            model1 = gltf.scene;
            let scFactor = 3;
            model1.scale.set(scFactor, scFactor, scFactor);
            model1.position.set(-20, 0, -30);
            group.add(model1);
            model1.traverse((node) => {
                if (node.isMesh && node.name !== 'Cube008') {
                    node.material = new THREE.MeshPhongMaterial({
                        color: this.colourArr[3],
                        specular: this.colourArr[1],
                        shininess: 20
                    });
                    node.castShadow = true;
                    node.receiveShadow = true;
                }
                if (node.isMesh && node.name === 'Cube008') {
                    node.material = new THREE.MeshPhongMaterial({
                        color: this.colourArr[0],
                        specular: this.colourArr[1],
                        shininess: 10
                    });
                    node.scale.set(8, 8, 8);
                    node.position.set(0, 15, 0);
                    node.castShadow = true;
                    node.receiveShadow = true;
                }
            });
            let model2 = model1.clone();
            model2.position.set(-100, 0, -20);
            group.add(model2);

            let model3 = model1.clone();
            model3.position.set(70, 0, -40);
            model3.rotateY(-Math.PI * 0.1);
            group.add(model3);
        },
            (xhr) => {
                console.log((xhr.loaded / xhr.total * 100) + '% loaded')
            },
            (error) => {
                console.log(error);
            });
    }

    setupGeometry() {
        this.setupGround();
        this.scatterRandomGeometry();
    }

    setupGround() {
        this.groundMap = this.loader.load(texture2);
        this.groundMap.anisotropy = 16;

        const groundPlane = new THREE.Mesh(new THREE.PlaneGeometry(1000, 1000), new THREE.MeshStandardMaterial({
            color: this.colourArr[4],
            map: this.groundMap,
            bumpMap: this.groundMap,
            bumpScale: 0.05,
            metalness: 0,
            roughness: 1
        }));

        groundPlane.receiveShadow = true;
        groundPlane.position.y = 0;
        groundPlane.quaternion.setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
        this.scene.add(groundPlane);
    }

    scatterRandomGeometry() {
        this.meshElems = new THREE.Object3D();
        this.meshElems.position.set(-150, 40, 0);
        this.scene.add(this.meshElems);

        let elemsNum = 20;
        let geo = new THREE.IcosahedronBufferGeometry(0.5, 0);
        let mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
            color: this.colourArr[1],
            map: this.groundMap,
            transparent: true,
            opacity: 0.5
        }));
        mesh.receiveShadow = true;
        this.meshElems.add(mesh);

        //spiral 
        let stepAngle = Math.PI * 2 / elemsNum;
        let xStep = 0.8;
        let turns = 20;
        for (let i = 0; i < turns * elemsNum; i++) {
            let elem = mesh.clone();
            let angle = stepAngle * i;
            let dist = 10 + Math.random() * 10; //the distance to the center of the axis
            let s = 1 + Math.random() * 2;

            elem.position.y = Math.sin(angle) * dist;
            elem.position.x = xStep * i; //Math.cos(angle)*dist;
            elem.position.z = Math.cos(angle) * dist; //Math.random()*5;

            elem.rotation.z = angle + Math.PI / 2;
            elem.scale.set(Math.sin(Math.random() + i * 10) + 10, s, s);
            this.meshElems.add(elem);
        }
    }

    setupSound() {
        let sound = new Sound();
        sound.start(this.renderer.domElement);
        sound.setup();
        sound.createSinths();
        this.sound = sound;
    }

    raycast() {
        this.raycaster.setFromCamera(this.mouse, this.camera);
        let intersects = this.raycaster.intersectObjects(this.scene.children, true);
        if (intersects.length > 0 && typeof this.scene != "undefined" && intersects[0].object.isMesh) {
            if (intersects[0].object.name === 'Cube008') {
                this.oscillate(intersects[0].object);
                this.sound.playOnTrigger();
            }
        }
    }

    orbitCamera(rotSpeed) {
        let x = this.camera.position.x;
        let z = this.camera.position.z;

        this.camera.position.x = x * Math.cos(rotSpeed) + z * Math.sin(rotSpeed);
        this.camera.position.z = z * Math.cos(rotSpeed) - x * Math.sin(rotSpeed);

        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    oscillate(mesh) {
        this.isAnimate = true;
        const originalScaleX = mesh.scale.z;
        const originalScaleY = mesh.scale.y;
        const originalScaleZ = mesh.scale.z;
        const easing = 'elastic';

        let oscil = gsap.timeline();
        oscil.to(mesh.scale, 1, { x: 1.02, y: 2.05, z: 1.02, ease: easing })
            .to(mesh.scale, 1, { x: originalScaleX, y: originalScaleY, z: originalScaleZ, ease: easing });

        oscil.eventCallback("onComplete", () => {
            this.isAnimating = false;
        });

        oscil.play();
    }

    updateSceneSubjects(time) {
        for (let i = 0; i < this.meshElems.children.length; i++) {
            let elem = this.meshElems.children[i];
            elem.rotation.z += Math.random() * .005; //*(i+1);
            elem.rotation.y += Math.random() * .02; //*(i+1);
        }
    }

    update() {
        if (this.isDisposed) return;
        let delta = this.clock.getDelta();
        if (isNaN(delta) || delta > 1000 || delta === 0) {
            delta = 1000 / 60;
        }
        if (this.controls) this.controls.update();
        this.updateSceneSubjects(delta);
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    onClick(e) {
        if (this.isDisposed) return;
        this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = - (e.clientY / window.innerHeight) * 2 + 1;
        this.raycast();
        this.update();
    }

    dispose() {
        this.isDisposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                child.material.dispose();
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
        this.sound.stop();
    }
}


